import { useCallback } from 'react';

import { type VehicleListing } from '@/services/vehicles/types';
import { MAX_CAR_ORDER, useCarOrderStore } from '@/stores/carOrderStore';

export type CarOrderEntryState = {
  /** The car is already in the visitor's cart. */
  added: boolean;
  /** The cart is at its maximum and this car is not in it. */
  blocked: boolean;
  /** Written for a button that shows its label. */
  label: string;
  /** Written for a button that shows only an icon. */
  shortLabel: string;
  toggle: () => void;
};

/**
 * One car's place in the cart. Every subscription is to a primitive, so a card
 * re-renders only when its own membership or the cart's fullness changes.
 */
export function useCarOrderEntry(vehicle: VehicleListing): CarOrderEntryState {
  const added = useCarOrderStore((s) => s.items.some((i) => i.slug === vehicle.slug));
  const count = useCarOrderStore((s) => s.items.length);
  const toggleItem = useCarOrderStore((s) => s.toggle);

  const blocked = !added && count >= MAX_CAR_ORDER;
  const toggle = useCallback(() => toggleItem(vehicle), [toggleItem, vehicle]);

  return {
    added,
    blocked,
    label: added ? 'In your cart' : blocked ? `Cart is full (${MAX_CAR_ORDER})` : 'Add to cart',
    shortLabel: added
      ? 'Remove from cart'
      : blocked
        ? `Cart is full (${MAX_CAR_ORDER} cars)`
        : 'Add to cart',
    toggle,
  };
}
